import type { QueryClient } from "@tanstack/react-query";
import { toInputFieldDefinition } from "@/domains/templates/mappers";
import { templateBuilderQueries } from "./templates";
import type {
  InputFieldDefinition,
  TemplateVariableTarget,
  Variable,
  StepWithSettings,
} from "@/domains/templates/types";

type CachedVariableResource = {
  variables?: Variable[];
  inputFields?: InputFieldDefinition[];
  scopedInputFields?: InputFieldDefinition[];
  inputResults?: Record<string, unknown>;
};

function sortVariables(variables: Variable[]): Variable[] {
  return [...variables].sort(
    (left, right) =>
      (left.variable_order ?? Number.MAX_SAFE_INTEGER) -
        (right.variable_order ?? Number.MAX_SAFE_INTEGER) ||
      left.variable_id.localeCompare(right.variable_id),
  );
}

function replaceResourceVariables<T extends CachedVariableResource>(
  resource: T,
  variables: Variable[],
): T {
  const sorted = sortVariables(variables);
  if (!resource.inputFields && !resource.scopedInputFields) {
    return { ...resource, variables: sorted };
  }

  const previousFields = new Map(
    (resource.scopedInputFields ?? []).flatMap((field) =>
      field.variableId ? [[field.variableId, field] as const] : [],
    ),
  );
  const scopedInputFields = sorted.map((variable) => {
    const definition = toInputFieldDefinition(variable);
    const previous = previousFields.get(variable.variable_id);
    return previous?.hasValue
      ? {
          ...definition,
          configuredValue: previous.configuredValue,
          hasValue: true,
        }
      : definition;
  });
  const variableNames = new Set(sorted.map((item) => item.variable_name));
  const inputResults = Object.fromEntries(
    Object.entries(resource.inputResults ?? {}).filter(([name]) =>
      variableNames.has(name),
    ),
  );

  return {
    ...resource,
    variables: sorted,
    scopedInputFields,
    inputFields: scopedInputFields.filter(
      (field) => field.parentVariableId === null,
    ),
    inputResults,
  };
}

function updateTargetCache(
  queryClient: QueryClient,
  target: TemplateVariableTarget,
  update: (variables: Variable[]) => Variable[],
): void {
  if (target.level === "analysis") {
    queryClient.setQueryData<CachedVariableResource>(
      templateBuilderQueries.detail(target.templateId).queryKey,
      (current) =>
        current
          ? replaceResourceVariables(current, update(current.variables ?? []))
          : current,
    );
    return;
  }

  queryClient.setQueryData<
    Array<
      CachedVariableResource & {
        id?: string;
        analysis_step_id?: string;
      }
    >
  >(templateBuilderQueries.steps(target.templateId).queryKey, (current) =>
    current?.map((section) =>
      (section.id ?? section.analysis_step_id) === target.stepId
        ? replaceResourceVariables(section, update(section.variables ?? []))
        : section,
    ),
  );
}

export function upsertCachedVariable(
  queryClient: QueryClient,
  target: TemplateVariableTarget,
  variable: Variable,
): void {
  upsertCachedVariables(queryClient, target, [variable]);
}

export function upsertCachedVariables(
  queryClient: QueryClient,
  target: TemplateVariableTarget,
  incoming: Variable[],
): void {
  const incomingById = new Map(
    incoming.map((variable) => [variable.variable_id, variable]),
  );
  updateTargetCache(queryClient, target, (variables) => [
    ...variables.filter((variable) => !incomingById.has(variable.variable_id)),
    ...incomingById.values(),
  ]);
}

export function removeCachedVariables(
  queryClient: QueryClient,
  target: TemplateVariableTarget,
  variableIds: string[],
): void {
  const removed = new Set(variableIds);
  updateTargetCache(queryClient, target, (variables) =>
    variables.filter((item) => !removed.has(item.variable_id)),
  );
}

export function insertCachedTemplateStep(
  queryClient: QueryClient,
  templateId: string,
  step: StepWithSettings,
): void {
  queryClient.setQueryData<StepWithSettings[]>(
    templateBuilderQueries.steps(templateId).queryKey,
    (current) =>
      [
        ...(current ?? []).map((item) =>
          item.analysis_order >= step.analysis_order
            ? { ...item, analysis_order: item.analysis_order + 1 }
            : item,
        ),
        step,
      ].sort((left, right) => left.analysis_order - right.analysis_order),
  );
}

export function applyCachedTemplateStepOrder(
  queryClient: QueryClient,
  templateId: string,
  order: Array<{ analysis_step_id: string; analysis_order: number }>,
): void {
  const orderById = new Map(
    order.map((item) => [item.analysis_step_id, item.analysis_order]),
  );
  queryClient.setQueryData<StepWithSettings[]>(
    templateBuilderQueries.steps(templateId).queryKey,
    (current) =>
      current
        ?.map((step) => ({
          ...step,
          analysis_order:
            orderById.get(step.analysis_step_id) ?? step.analysis_order,
        }))
        .sort((left, right) => left.analysis_order - right.analysis_order),
  );
}

export function removeCachedTemplateStep(
  queryClient: QueryClient,
  templateId: string,
  stepId: string,
): void {
  queryClient.setQueryData<StepWithSettings[]>(
    templateBuilderQueries.steps(templateId).queryKey,
    (current) => {
      const removed = current?.find((step) => step.analysis_step_id === stepId);
      if (!current || !removed) return current;
      return current
        .filter((step) => step.analysis_step_id !== stepId)
        .map((step) =>
          step.analysis_order > removed.analysis_order
            ? { ...step, analysis_order: step.analysis_order - 1 }
            : step,
        );
    },
  );
}
